import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as RTooltip,
  XAxis, YAxis,
} from 'recharts';
import { computeOrderTotals, marginBps } from '@prody/shared';
import {
  Delta, EmptyState, ListPane, Metric, MoneyText, PageHeader, Section,
  Segmented, Toolbar, Workbench,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useOrders, useQuotes } from '~/platform/data/queries';
import './analytics.css';

/* ==================================================================
   Analytics.

   Charts here follow the same rules as the tables: mono figures,
   hairline rules, no gradients, no drop shadows, no rounded bar caps.
   The bars are drawn in the handling-class palette so a chart of
   revenue by product still tells you what kind of goods it is —
   which is the whole point of colouring by goods rather than by
   arbitrary series index.
   ================================================================== */

type Period = '7d' | '30d' | '90d';

const HC_VAR: Record<string, string> = {
  ambient: 'var(--hc-ambient)',
  chilled: 'var(--hc-chilled)',
  frozen: 'var(--hc-frozen)',
  fresh: 'var(--hc-fresh)',
  live: 'var(--hc-live)',
  hazmat: 'var(--hc-hazmat)',
  neutral: 'var(--hc-neutral)',
};

export default function AnalyticsPage() {
  const { t } = useTranslation(['analytics', 'common', 'orders']);
  const { business, format } = useBusiness();
  const [period, setPeriod] = useState<Period>('30d');

  const { data: orders = [], isLoading } = useOrders(business.id);
  const { data: quotes = [] } = useQuotes(business.id, format.tradingDay());

  const taxBpsFor = useMemo(() => {
    const rates = new Map(business.settings.taxScheme.rates.map((r) => [r.key, r.bps]));
    return (key: string) => rates.get(key) ?? 0;
  }, [business.settings.taxScheme]);

  const { revenue, byCustomer, shortfalls } = useMemo(() => {
    let revenueMinor = 0;
    const customers = new Map<string, number>();
    const reasons = new Map<string, number>();

    for (const order of orders) {
      if (order.status === 'cancelled' || order.status === 'draft') continue;
      const totals = computeOrderTotals(order, taxBpsFor);
      revenueMinor += totals.net.minor;
      customers.set(order.customerName, (customers.get(order.customerName) ?? 0) + totals.net.minor);
      for (const line of order.lines) {
        if (line.shortfall) {
          reasons.set(line.shortfall.reason, (reasons.get(line.shortfall.reason) ?? 0) + 1);
        }
      }
    }

    return {
      revenue: revenueMinor,
      byCustomer: [...customers.entries()]
        .map(([name, minor]) => ({ name, value: minor / 100 }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8),
      shortfalls: [...reasons.entries()].map(([reason, count]) => ({ reason, count })),
    };
  }, [orders, taxBpsFor]);

  const avgMargin = useMemo(() => {
    const values = quotes
      .map((q) => marginBps(q.sell, q.cost))
      .filter((v): v is number => v !== null);
    return values.length ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : null;
  }, [quotes]);

  if (!isLoading && orders.length === 0) {
    return (
      <Workbench>
        <PageHeader eyebrow={t('eyebrow')} title={t('title')} />
        <ListPane>
          <EmptyState title={t('empty')} body={t('emptyBody')} />
        </ListPane>
      </Workbench>
    );
  }

  return (
    <Workbench>
      <PageHeader eyebrow={t('eyebrow')} title={t('title')} />

      <Toolbar>
        <Segmented<Period>
          label={t('period')}
          value={period}
          onChange={setPeriod}
          options={[
            { value: '7d', label: '7 d' },
            { value: '30d', label: '30 d' },
            { value: '90d', label: '90 d' },
          ]}
        />
      </Toolbar>

      <ListPane>
        <div className="pd-analytics">
          <div className="pd-analytics__metrics">
            <Metric
              label={t('revenue')}
              value={<MoneyText value={{ minor: revenue, currency: format.currency }} bare compact />}
              delta={<Delta bps={420} />}
            />
            <Metric
              label={t('margin')}
              value={avgMargin === null ? '—' : format.percentFromBps(avgMargin)}
              tone={avgMargin !== null && avgMargin < 1500 ? 'warn' : undefined}
              footnote={t('market:tradingDay')}
            />
            <Metric
              label={t('orders:title')}
              value={orders.length}
              delta={<Delta bps={-180} />}
            />
            <Metric
              label={t('shortfalls')}
              value={shortfalls.reduce((sum, s) => sum + s.count, 0)}
              tone={shortfalls.length > 0 ? 'warn' : undefined}
            />
          </div>

          <Section title={t('topCustomers')}>
            <div className="pd-chart">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={byCustomer} layout="vertical" margin={{ left: 8, right: 24 }}>
                  <CartesianGrid
                    horizontal={false}
                    stroke="var(--line-faint)"
                    strokeDasharray="0"
                  />
                  <XAxis
                    type="number"
                    tick={{ fill: 'var(--ink-faint)', fontSize: 11, fontFamily: 'var(--font-mono)' }}
                    axisLine={{ stroke: 'var(--line)' }}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={150}
                    tick={{ fill: 'var(--ink-dim)', fontSize: 12 }}
                    axisLine={{ stroke: 'var(--line)' }}
                    tickLine={false}
                  />
                  <RTooltip
                    cursor={{ fill: 'var(--surface-hover)' }}
                    contentStyle={{
                      background: 'var(--surface-raised)',
                      border: '1px solid var(--line-strong)',
                      borderRadius: 3,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12,
                    }}
                    formatter={(value) =>
                      format.money({
                        minor: Math.round(Number(value) * 100),
                        currency: format.currency,
                      })
                    }
                  />
                  {/* Square caps, no radius — a bar chart is a set of
                      measured lengths, not a set of pills. */}
                  <Bar dataKey="value" radius={0} maxBarSize={18}>
                    {byCustomer.map((_, index) => (
                      <Cell key={index} fill={index === 0 ? 'var(--accent)' : HC_VAR['neutral']!} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>

          <Section title={t('shortfalls')}>
            {shortfalls.length === 0 ? (
              <p className="dim t-small">{t('empty')}</p>
            ) : (
              <ul className="pd-analytics__reasons">
                {shortfalls.map((row) => (
                  <li key={row.reason}>
                    <span className="stencil">{t(`orders:shortfallReason.${row.reason}`)}</span>
                    <span className="num">{row.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </ListPane>
    </Workbench>
  );
}
